import { useMemo } from "react";

import { formatNumber } from "@utilifeed/core";

import { roundNumber } from "../utils/convertKwhToMwh";
import type { TableRowData } from "./types";

type Substation = { uid: string; name?: string | null };

export type UsePropertiesTableDataInput = Readonly<{
  substations: Substation[] | null | undefined;
  addressByUid?: Map<string, string | null | undefined>;
  flexStationIdByUid?: Map<string, string | null | undefined>;
  secondaryIdByUid?: Map<string, string | null | undefined>;
  numberOfSensorsByUid?: Map<string, number>;
  targetTempByUid?: Map<string, number | null | undefined>;
  /** Last-1-hour average shown in the "Current average" column. */
  currentTempByUid?: Map<string, number | null | undefined>;
  avgTemp24hByUid?: Map<string, number | null | undefined>;
  energyMwhByUid?: Map<string, number | null | undefined>;
  loading?: { flex?: boolean; temps?: boolean; energy?: boolean };
}>;

const NA = "N/A";

function formatTemp(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NA;
  return formatNumber(value);
}

function formatEnergy(value: number | null | undefined): string {
  const mwh = value ?? 0;
  return formatNumber(roundNumber(mwh));
}

export function usePropertiesTableData({
  substations,
  addressByUid,
  flexStationIdByUid,
  secondaryIdByUid,
  numberOfSensorsByUid,
  targetTempByUid,
  currentTempByUid,
  avgTemp24hByUid,
  energyMwhByUid,
  loading,
}: UsePropertiesTableDataInput): TableRowData[] {
  return useMemo(() => {
    if (!substations) return [];

    const isLoadingFlex = !!loading?.flex;
    const isLoadingTemps = !!loading?.temps;
    const isLoadingEnergy = !!loading?.energy;

    return substations
      .filter((s): s is Substation => !!s)
      .map((s) => {
        const address = addressByUid?.get(s.uid) ?? s.name ?? null;
        const flexStationId = flexStationIdByUid?.get(s.uid) ?? null;
        const secondaryId = secondaryIdByUid?.get(s.uid) ?? null;
        const sensorCount = numberOfSensorsByUid?.get(s.uid) ?? 0;
        const targetTemp = targetTempByUid?.get(s.uid);
        const currentTemp = currentTempByUid?.get(s.uid);
        const avg24h = avgTemp24hByUid?.get(s.uid);
        const energyMwh = energyMwhByUid?.get(s.uid);

        return {
          uid: s.uid,
          name: s.name ?? "",
          address,
          flexStationId: flexStationId || NA,
          secondaryId: secondaryId || NA,
          numberOfSensors: formatNumber(sensorCount),
          targetTemp:
            targetTemp !== null && targetTemp !== undefined ? formatNumber(targetTemp) : NA,
          currentAvgTemp: formatTemp(currentTemp),
          last24hAvgTemp: formatTemp(avg24h),
          energyUse: formatEnergy(energyMwh),
          isLoadingFlex,
          isLoadingTemps,
          isLoadingEnergy,
        };
      });
  }, [
    substations,
    addressByUid,
    flexStationIdByUid,
    secondaryIdByUid,
    numberOfSensorsByUid,
    targetTempByUid,
    currentTempByUid,
    avgTemp24hByUid,
    energyMwhByUid,
    loading?.flex,
    loading?.temps,
    loading?.energy,
  ]);
}
